import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ACCOUNTS, AI_ACTIONS, AI_BRIEFING, EVIDENCE, QUEUE } from './mock-data';
import { mockRequest } from '../utils/mock';
import type { AiAction, AiBriefing, AiNarrative, NarrativeSegment } from '../models/ai.model';
import type { QueueItem } from '../models/queue.model';
import type { EvidenceType } from '../models/evidence.model';

const fmtCurrency = (n: number) => `$${(n / 1_000_000).toFixed(1)}M`;

@Injectable({ providedIn: 'root' })
export class CopilotService {
  getBriefing(): Observable<AiBriefing> {
    return mockRequest(AI_BRIEFING, 400);
  }

  getQueue(): Observable<QueueItem[]> {
    return mockRequest(QUEUE, 300);
  }

  getActions(): Observable<AiAction[]> {
    return mockRequest(AI_ACTIONS, 200);
  }

  /** Builds the AI risk-synthesis narrative as structured segments. */
  getNarrative(accountId: string): Observable<AiNarrative> {
    const account = ACCOUNTS.find((a) => a.id === accountId);
    const evidence = EVIDENCE[accountId];
    const segments: NarrativeSegment[] = [];

    if (account && evidence) {
      segments.push(
        { kind: 'text', text: `${account.name} presents a ` },
        { kind: 'entity', text: `${account.riskLabel.toLowerCase()}-risk (Score: ${account.riskScore})`, evidence: 'risk', hint: 'Risk Assessment Engine' },
        { kind: 'text', text: `, financially stable general contractor with ${account.yearsInBusiness} years of operating history. ` },
        { kind: 'emphasis', text: 'Action Required:', tone: 'warning' },
        { kind: 'text', text: ' The requested ' },
        { kind: 'entity', text: `Total Insured Value (${fmtCurrency(evidence.authority.requested)})`, evidence: 'authority', hint: 'Authority Check: Total Insured Value' },
        { kind: 'text', text: ` exceeds your Senior UW authority limit by ${evidence.authority.exceedsPct}%. ` },
        { kind: 'text', text: 'However, the underlying metrics support writing the business. Excluding a ' },
        { kind: 'trace', text: 'single $88.3K GL claim in 2023', documentId: 'src-loss-2023' },
        { kind: 'text', text: `, their five-year loss ratio is highly favorable (${account.lossRatio}%). A previously flagged ` },
        { kind: 'entity', text: 'address variance', evidence: 'enrichment', hint: 'Data Enrichment & Matching' },
        { kind: 'text', text: ' was automatically cleared via CoreLogic. ' },
        { kind: 'text', text: 'Proposed Solution: I have prepared a ' },
        { kind: 'entity', text: `technical pricing adjustment to ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(evidence.pricing.rows.reduce((s, r) => s + r.proposed, 0))}`, evidence: 'pricing', hint: 'Technical Rating vs. Proposed' },
        { kind: 'text', text: " to align with the broker's target and drafted the authority override justification for your manager, David Park." },
      );
    } else {
      segments.push({ kind: 'text', text: 'No AI narrative is available for this account yet.' });
    }

    return mockRequest({ accountId, segments }, 350);
  }

  /** Evidence type referenced by the narrative entity. */
  evidenceFor(entity: { evidence: EvidenceType }): EvidenceType {
    return entity.evidence;
  }

  /** Mock natural-language command handler. */
  ask(query: string): Observable<string> {
    const q = query.toLowerCase();
    let reply = 'I understand the request. Try "show high priority", "show blocked accounts", or "run a risk analysis".';
    if (q.includes('high priority')) reply = 'Showing 2 high-priority submissions: Ferris Bros. Construction LLC and Ironclad Metal Works. Both require human judgment before quoting.';
    else if (q.includes('blocked')) reply = '2 accounts are blocked: Nolan Logistics (unreadable SOV) and Coastal Warehousing (missing 3 years of loss runs). Chasers are drafted in the Action Hub.';
    else if (q.includes('risk')) reply = 'I can re-run the Risk Assessment Engine for the active account. Use the "Run Risk Analysis" action.';
    else if (q.includes('quote')) reply = '5 straight-through renewals are ready for bulk quoting. Use "Bulk Issue 5 Quotes" to send them to the brokers.';
    else if (q.includes('ferris')) reply = 'Ferris Bros. Construction LLC — moderate risk (72), TIV $18.4M exceeds authority by 23%, proposed premium $193,300. Referral drafted for David Park.';
    return of(reply).pipe(delay(600));
  }
}
